import Button from 'components/crm/ui/Button';
import DateField from 'components/crm/ui/DateField';
import Switch from 'components/crm/ui/Switch';
import TextField from 'components/crm/ui/TextField';
import { crmTaskErrorText } from 'helper/crmTaskErrors';
import { addDays, fromIsoDay, isoDay } from 'helper/timeLabels';
import useCrmContext from 'hooks/useCrmContext';
import useText from 'hooks/useText';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';

const pad = (value) => String(value).padStart(2, '0');
export const clockOf = (date) =>
  `${pad(date.getHours())}:${pad(date.getMinutes())}`;

// The local moment of `day` (YYYY-MM-DD) at `clock` (HH:MM), or null.
export const momentOf = (day, clock) => {
  const [year, month, date] = (day ?? '').split('-').map(Number);
  const match = /^(\d{1,2}):(\d{2})$/.exec(clock ?? '');
  if (!year || !month || !date || !match) return null;
  return new Date(year, month - 1, date, Number(match[1]), Number(match[2]));
};

// The local day `days` after `day` (both YYYY-MM-DD).
export const shiftDay = (day, days) => isoDay(addDays(fromIsoDay(day), days));

const daysBetween = (from, to) =>
  from && to ? Math.round((fromIsoDay(to) - fromIsoDay(from)) / 86400000) : 0;

// The form of an appointment: its days are shown with the last one included,
// the api's endDay of a whole day being the day after.
const formOf = (item) => {
  const start = new Date(item.startsAt);
  const end = new Date(item.endsAt);
  if (item.allDay) {
    const startDay = item.startDay ?? isoDay(start);
    return {
      allDay: true,
      startDay,
      endDay: item.endDay ? shiftDay(item.endDay, -1) : startDay,
      start: '09:00',
      end: '10:00',
    };
  }
  return {
    allDay: false,
    startDay: isoDay(start),
    endDay: isoDay(end),
    start: clockOf(start),
    end: clockOf(end),
  };
};

/**
 * « Modifier » of an appointment, in place of its detail: its title, its
 * location, the whole day or a slot (over one day or several), its
 * description. Saves through updateAppointment and hands the detail returned
 * to `onSaved`; « Annuler » or Échap give back the detail untouched. A title
 * is required only without a company, as on the api.
 */
const AppointmentEditForm = ({ item, onSaved, onCancel }) => {
  const { text } = useText('appointment');
  const { text: planningText } = useText('planning');
  const { text: tasksText } = useText('tasks');
  const { call } = useCrmContext();
  const [form, setForm] = useState(() => ({
    title: item.title ?? '',
    location: item.location ?? '',
    description: item.description ?? '',
    ...formOf(item),
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  // Bubbling, after the calendar of the day field: an Échap that closes the
  // calendar (default prevented) leaves the form open.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !event.defaultPrevented)
        cancelRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const set = (field) => (event) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));
  // The span is kept when the first day moves, as in Google Calendar.
  const setStartDay = (startDay) =>
    setForm((current) => ({
      ...current,
      startDay,
      endDay: shiftDay(startDay, daysBetween(current.startDay, current.endDay)),
    }));
  const startsAt = momentOf(form.startDay, form.start);
  const endsAt = momentOf(form.endDay, form.end);
  const timeKnown = form.allDay
    ? Boolean(form.startDay && form.endDay)
    : Boolean(startsAt && endsAt);
  const timeOk = form.allDay
    ? timeKnown && form.endDay >= form.startDay
    : timeKnown && endsAt > startsAt;
  const timeError =
    timeKnown && !timeOk ? tasksText('errors.INVALID_APPOINTMENT_TIME') : null;
  const titleOk =
    Boolean(form.title.trim()) || Boolean(item.companyId || item.company);
  const canSave = timeOk && titleOk && !busy;

  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    setError('');
    const slot = form.allDay
      ? {
          allDay: true,
          startDay: form.startDay,
          endDay: shiftDay(form.endDay, 1),
        }
      : {
          allDay: false,
          startsAt: startsAt.toISOString(),
          endsAt: endsAt.toISOString(),
        };
    try {
      const updated = await call(crmApi.updateAppointment, {
        id: item.id,
        fields: {
          title: form.title.trim(),
          location: form.location.trim(),
          ...slot,
          description: form.description.trim(),
        },
      });
      toast.success(text('updated'));
      onSaved(updated);
    } catch (failure) {
      const message = crmTaskErrorText(failure, tasksText);
      if (message) setError(message);
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <TextField
        id="appointment-edit-title"
        label={planningText('titleLabel')}
        value={form.title}
        maxLength={200}
        onChange={set('title')}
      />
      <TextField
        id="appointment-edit-location"
        label={text('locationLabel')}
        value={form.location}
        maxLength={200}
        onChange={set('location')}
      />
      <Switch
        checked={form.allDay}
        onChange={(allDay) => setForm((current) => ({ ...current, allDay }))}
        label={text('allDayLabel')}
      />
      <div className="grid grid-cols-2 gap-3">
        <DateField
          id="appointment-edit-start-day"
          label={text('startDayLabel')}
          value={form.startDay}
          onChange={setStartDay}
        />
        {!form.allDay && (
          <TextField
            id="appointment-edit-start"
            type="time"
            label={text('startLabel')}
            value={form.start}
            onChange={set('start')}
          />
        )}
        <div className="flex flex-col gap-2">
          <DateField
            id="appointment-edit-end-day"
            label={text('endDayLabel')}
            value={form.endDay}
            minDate={fromIsoDay(form.startDay || isoDay(new Date()))}
            onChange={(endDay) =>
              setForm((current) => ({ ...current, endDay }))
            }
          />
          {form.allDay && timeError && (
            <p className="text-sm text-danger">{timeError}</p>
          )}
        </div>
        {!form.allDay && (
          <TextField
            id="appointment-edit-end"
            type="time"
            label={text('endLabel')}
            value={form.end}
            onChange={set('end')}
            error={timeError}
          />
        )}
      </div>
      <TextField
        id="appointment-edit-description"
        multiline
        rows={4}
        label={planningText('descriptionLabel')}
        value={form.description}
        maxLength={5000}
        onChange={set('description')}
      />
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>
          {text('cancelEdit')}
        </Button>
        <Button disabled={!canSave} onClick={save}>
          {text('saveEdit')}
        </Button>
      </div>
    </div>
  );
};

export default AppointmentEditForm;
